import { TypedDocumentNode, gql } from '@apollo/client';
import styled from 'styled-components';

import { useTypedParams } from '@sorare/routing';

import { FullWidthContainer } from '@sorare/core/src/atoms/layout/FullWidthContainer';
import { LoadingIndicator } from '@sorare/core/src/atoms/loader/LoadingIndicator';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';
import { randomizedPollInterval } from '@sorare/core/src/lib/gql';

import { NoCardEntry } from '@sorare/gameplay/src/components/league/NoCardEntry';
import { useDeleteLineup } from '@sorare/gameplay/src/hooks/so5/useDeleteLineup';
import {
  getFixtureQueryVariables,
  isFixtureOpened,
} from '@sorare/gameplay/src/lib/so5';

import { UpcomingLeaderboards } from 'components/lobby/Leaderboards/upcoming';

import type {
  BaseballPlayProFixtureCompetitionsPageQuery,
  BaseballPlayProFixtureCompetitionsPageQueryVariables,
} from './__generated__/page.graphql';
import { PageParams } from './__generated__/routeParams';

const LobbySection = styled(FullWidthContainer)`
  margin-block: var(--double-unit) 0;
  &:empty {
    display: none;
  }
`;

const Wrapper = styled.div`
  margin-bottom: var(--double-unit);
`;

const BASEBALL_PLAY_PRO_FIXTURE_COMPETITIONS_PAGE_QUERY = gql`
  query BaseballPlayProFixtureCompetitionsPageQuery(
    $slug: String
    $type: So5State
  ) {
    so5 {
      so5Fixture(type: $type, slug: $slug, sport: BASEBALL) {
        slug
        aasmState
        startDate
        endDate
        gameWeek
        sport
        type
        so5Leaderboards {
          slug
          rarityType
          canCompose {
            value
            notEnoughEligibleCards
          }
          ...UpcomingLeaderboards_So5Leaderboard
        }
      }
    }
  }
  ${UpcomingLeaderboards.fragments.So5Leaderboard}
` as TypedDocumentNode<
  BaseballPlayProFixtureCompetitionsPageQuery,
  BaseballPlayProFixtureCompetitionsPageQueryVariables
>;

const POLL_INTERVAL = randomizedPollInterval(5);

const useProPlayPageQuery = (skip?: boolean) => {
  const { fixture } = useTypedParams<PageParams>();
  const variables = getFixtureQueryVariables(fixture);
  const query = useQuery(BASEBALL_PLAY_PRO_FIXTURE_COMPETITIONS_PAGE_QUERY, {
    pollInterval: POLL_INTERVAL,
    skip,
    variables,
  });

  return query;
};

const PlayProPage = () => {
  const deleteLineup = useDeleteLineup();
  const { data, loading } = useProPlayPageQuery();

  const { so5Fixture } = data?.so5 || {};

  if (!data || (!data && loading) || !so5Fixture) {
    return <LoadingIndicator fullHeight />;
  }

  const isUpcoming = isFixtureOpened(so5Fixture);

  const displayedLeaderboards = isUpcoming
    ? so5Fixture.so5Leaderboards.filter(
        ({ canCompose }) =>
          canCompose.value || canCompose.notEnoughEligibleCards
      )
    : so5Fixture.so5Leaderboards;

  return (
    <>
      <LobbySection />
      <FullWidthContainer>
        <Wrapper>
          <UpcomingLeaderboards
            onDelete={deleteLineup}
            globalLeaderboards={displayedLeaderboards}
          />
          <NoCardEntry sport={so5Fixture.sport} eventType={so5Fixture.type} />
        </Wrapper>
      </FullWidthContainer>
    </>
  );
};

export default PlayProPage;
